import { ChangeDetectorRef } from '@angular/core';
import { SearchWrapper } from '../services/xml/xml.service';
import { DataArea } from './data-area';
import { DrObject, DrObjectType, SupportedRegExType } from './dr-object';
import { Endpoint, ReviewSystem, SettingType, SizeUnits } from './enums';
import { ExportMetadataProfile } from './export-metadata-profile';
import { ExportStream } from './export-stream';
import { Tag, TagApplication } from './tag';
import { TaskStatusSection } from './task';
import { Template } from './template';
import { Timezone } from './timezone';
import { DrNamedQuery } from './dr-named-query';

export enum EmailOptions {
    TAGGED_CONTENT_ONLY = 'TAGGED_CONTENT_ONLY',
    INCLUDE_MAGS = 'INCLUDE_MAGS',
    INCLUDE_THREADS = 'INCLUDE_THREADS'
}

export enum EmailOutputFormat {
    NATIVE_FORMAT = 'NATIVE_FORMAT',
    HTML_FORMAT = 'HTML_FORMAT',
    MHTML_FORMAT = 'MHTML_FORMAT',
    HTML_MHTML_FORMAT = 'HTML_MHTML_FORMAT',
    PDF_FORMAT = 'PDF_FORMAT'
}

export enum BegAttachMode {
    PARENT_EMAIL = 'PARENT_EMAIL',
    FIRST_ATTACHMENT = 'FIRST_ATTACHMENT'
}

export enum ExportDuplicateMode {
    ALL_DOCUMENTS = 'ALL_DOCUMENTS',
    ALL_DOCUMENT_REFERENCES = 'ALL_DOCUMENT_REFERENCES',
    UNIQUE_DOCUMENTS = 'UNIQUE_DOCUMENTS'
}

export enum TiffMode {
    SINGLE_PAGE = 'SINGLE_PAGE',
    MULTI_PAGE = 'MULTI_PAGE'
}

export enum StampType {
    NONE = 'NONE',
    BATES = 'BATES',
    METADATA = 'METADATA',
    TEXT = 'TEXT',
    CODE_STAMP = 'CODE_STAMP'
}

export enum ExportTarget {
    RELATIVITY = 'RELATIVITY',
    FILE = 'FILE'
}

export class ExportStreamOptions {
    dataAreaHandle: string = null;
    dataAreaName: string = null;
    documentsToExport: string = null;
    metadataTemplateHandle: string = null;
    metadataTemplateName: string = null;
    overlayMetadataTemplateHandle: string = null;
    overlayMetadataTemplateName: string = null;
    generateConcordance = false;
    generateEdrmXml = false;
    generateCsv = false;
    generateLst = false;
    generateDii = false;
    utcOffset: number;
    timezone: string = null;
    dateFormat: string = null;
    timeFormat: string = null;
    dateTimeDelimiter: string = null;
    manifestSizeUnits: SizeUnits;
    emailOptions: EmailOptions;
    includeContainerReferences = false;
    includeSeperateEmailAttachments = false;
    emailKeepOriginalFormat: boolean;
    emailOutputFormat: EmailOutputFormat;
    volumeLabel: string = null;
    documentIdPrefix: string = null;
    startingDocumentId: number;
    documentIdPadSize: number;
    manifestOnly = false;
    exportToDb = false;
    begAttachMode: BegAttachMode;
    includeThreadAttach = false;
    maxManifestRecords: number;
    exportDuplicateMode: ExportDuplicateMode;
    includeNearDuplicates = false;
    nearDuplicatesThreshold: number;
    nearDuplicatesMinTermCount: number;
    nearDuplicatesProcessAttachments: boolean;
    includeNativeFiles = false;
    includeTextConversion = false;
    includePdfConversion = false;
    includePdfOLEChildren = false;
    includeOLEChildren = false;
    includeTiffConversion = false;
    nativeSubdirectoryName: string = null;
    textSubdirectoryName: string = null;
    pdfSubdirectoryName: string = null;
    oleSubdirectoryName: string = null;
    tiffSubdirectoryName: string = null;
    basePath: string = null;
    includeVolumeLabelInPath = false;
    tiffMode: TiffMode;
    copyNativePdfFile: boolean;
    copyNativeTextFile: boolean;
    excludeEmailHeaders = false;
    excludeEmailHeadersInLoadFile = false;
    redactedDocumentsOnly: boolean;
    maxTiffPages: number;
    tiffBW: boolean;
    pageSeperator: string = null;
    pagePadSize: number;
    pageLevelNumbering = false;
    includeStamp = false;
    stampFontSize: number;
    stampFontName: string;
    stampTopLeftType: StampType;
    stampTopLeftLine: number;
    stampTopLeftValue: string;
    stampTopCenterType: StampType;
    stampTopCenterLine: number;
    stampTopCenterValue: string;
    stampTopRightType: StampType;
    stampTopRightLine: number;
    stampTopRightValue: string;
    stampBottomLeftType: StampType;
    stampBottomLeftLine: number;
    stampBottomLeftValue: string;
    stampBottomCenterType: StampType;
    stampBottomCenterLine: number;
    stampBottomCenterValue: string;
    stampBottomRightType: StampType;
    stampBottomRightLine: number;
    stampBottomRightValue: string;
    showRedactionLabels = false;
    includeSlipsheet = false;
    slipsheetName: string;
    custodianReportQueries: Array<string>;
    expandFields = false;
    maximumEmbeddedTextSize: number;
    truncateEmbeddedText: boolean;
    concordanceUnicodeFormat = false;
    concordanceSeparatorChar: string;
    concordanceNewlineChar: string;
    concordanceQuoteChar: string;
    convertImagesToPdf = false;
    tagList: Array<TagApplication>;
    exportTarget: ExportTarget;
    getkCuraConnectorHandle: string;
    getkCuraCaseName: string;
    getkCuraDataAreaHandle: string;
    getkCuraMappingHandle: string;
    separateDupeManifest = false;
    postProcess = false;
    postProcessDataAreaHandle: string;
    copyImagePdfs = false;
    removeAttachedArchives = false;
    redactedDocuments = false;
    overlayManifest = false;
    separateImageExceptions = false;
    docIdsByPrefix: any;
    srcViewHandle: string;
    tableName: string = null;
    taskStatus: Array<TaskStatusSection>;
    includeAllMasterDuplicates: boolean;
    highlightTerms: boolean;
    generateSearchReports: boolean;
    custodianQueriesFileName: string = null;
    custodianQueriesFileData: string = null;
    fileName: string = null;
    reviewFolderId: number = -1;
    createReviewFolder: boolean;
    reviewFolderName: string;
    reviewFolderPath: string;
    folderByMetadataField: string;
    reviewProjectId: number;
    reviewSystem: string;
    includeFullText = false;
    maxRecordsPerFile: boolean;
    delimiters: any;
    startingDocumentIdDisplay: string;
    folderSize: number;
    exportImageExceptionsSet: Array<DrNamedQuery>;
    exportNearDupeNumericsIncluded: boolean;
    exportExtensionConversionEnabled: boolean;
    exportVolumesAllowed: boolean;
    allExportDatabaseConnections: Array<any>;
    exportDatabaseConnectionHandle: string;
    nativeSubdirectoryInput: string;
    textSubdirectoryInput: string;
    pdfSubdirectoryInput: string;
    settingType: SettingType;
    metadataSettingsTemplateHandle: string;
    initialStartingDocumentId: number;
    updatedStartingDocumentId: number;
    postProcessKey: string;

    constructor(value?: Partial<ExportStreamOptions>) {
        if (value) {
            if (value.tagList) {
                value.tagList = value.tagList.map(tag => new TagApplication(tag));
            }
            Object.assign(this, value);
        }
    }
}

export enum StampPosition {
    TopLeft = 'TopLeft',
    TopCenter = 'TopCenter',
    TopRight = 'TopRight',
    BottomLeft = 'BottomLeft',
    BottomCenter = 'BottomCenter',
    BottomRight = 'BottomRight'
}

export class ProductionStamp {
    position: StampPosition;
    positionLabel: string;
    automationId: string;

    stampType: StampType;
    private _stampTypeEdit: StampType;
    get stampTypeEdit(): StampType {
        if (!this._stampTypeEdit) {
            this._stampTypeEdit = this.stampType || StampType.NONE;
        }
        return this._stampTypeEdit;
    }
    set stampTypeEdit(val: StampType) {
        this._stampTypeEdit = val;
        delete this._stampValueEdit;
    }

    stampLine: number;
    stampLineEdit: number;

    stampValue: string;
    private _stampValueEdit: string;
    get stampValueEdit(): string {
        if (!this._stampValueEdit) {
            this._stampValueEdit = this.stampValue;
        }
        return this._stampValueEdit;
    }
    set stampValueEdit(val: string) {
        this._stampValueEdit = val;
    }

    stampTypeAttribute: string;
    stampLineAttribute: string;
    stampValueAttribute: string;

    constructor(value: Partial<ProductionStamp>) {
        if (value && value.position) {
            Object.assign(this, value);
            this.positionLabel = this.position.replace(/([a-z])([A-Z])/g, '$1 $2');
            this.automationId = `-production-stamping-${this.positionLabel.replace(/\s+/g, '-').toLowerCase()}`;
            this.stampTypeAttribute = 'stamp' + this.position + 'Type';
            this.stampLineAttribute = 'stamp' + this.position + 'Line';
            this.stampValueAttribute = 'stamp' + this.position + 'Value';
        }
    }
}

export class ExportUpdateDate {
    name: string;
    displayName: string;
    selected: boolean;

    constructor(value: Partial<ExportUpdateDate>) {
        Object.assign(this, value);
        if (this.name) {
            const datetime = this.name.split('-');
            this.displayName = `${datetime[0]}-${datetime[1]}-${datetime[2]} ${datetime[3]}:${datetime[4]}:${datetime[5]}`;
        }
    }
}

export interface ExportExtraOptions {
    isBusy: Array<Endpoint>;
    exportStreams: Array<ExportStream>;
    exportStreamNames: Array<string>;
    exportStreamName: string;
    exportDataAreas: Array<DataArea>;
    exportFieldTemplates: {
        project: Array<ExportMetadataProfile | Template>,
        organization: Array<ExportMetadataProfile | Template>,
        system: Array<ExportMetadataProfile | Template>
    };
    exportSettingsTemplates: {
        project: Array<ExportMetadataProfile | Template>,
        organization: Array<ExportMetadataProfile | Template>,
        system: Array<ExportMetadataProfile | Template>
    };
    includeFullText: boolean;
    selectedExportDatabaseConnection: any;
    maxRecordsPerFile: boolean;
    consolidatedManifest?: boolean;
    timezones: Array<Timezone>;
    allTimezones: Array<Timezone>;
    prevSelectedTimezone: string;
    dateFormats: Array<string>;
    delimiters: Array<string>;
    timeFormats: Array<string>;
    documentPrefixes: Array<string>;
    startingDocumentIdDisplay: string;
    generateSearchReports: boolean;
    metadataProperties: Array<string>;
    updateDates?: Array<ExportUpdateDate>;
    includeMetadata?: boolean;
}

export interface ExportOnChange {
    exportStreamName: (name: string, isItemClick: boolean) => void;
    emailOptions: (emailOptions: EmailOptions) => void;
    tableName: {
        keyboard: () => void;
        paste: (text: string) => void;
    };
    includeSeperateEmailAttachments: (includeSeperateEmailAttachments?: boolean, onInitialLoadOfExportSettingsPage?: boolean) => void;
    includePdfOLEChildren: () => void;
    includeFullText: () => void;
    includeContainerReferences: (includeContainerReferences: boolean, isExportSettingsPageLoading?: boolean) => void;
    exportDuplicateMode: () => void;
    duplicateOverlays: () => void;
    includeNearDuplicates: () => void;
    nearDuplicatesProcessAttachments: () => void;
    outputOptions: (optionName: string) => void;
    maxRecordsPerFile: () => void;
    postProcess: () => void;
    selectedTimezone: () => void;
    productionSettingType: (optionName: string, onInitialLoadOfExportSettingsPage?: boolean) => void;
    consolidatedManifest?: () => void;
    idPrefix: (id: string) => void;
    dateFormat: (id: string) => void;
    dateTimeDelimiter: (id: string) => void;
    timeFormat: (id: string) => void;
    createReviewSystemFolder: () => void;
    removeAttachedArchives: () => void;
    excludeEmailHeadersInLoadFile: () => void;
    separateDupeManifest: () => void;
    overlayManifest: () => void;
    includeAllMasterDuplicates: () => void;
    overlayMetadataTemplateHandle: () => void;
    begAttachMode: () => void;
    manifestSizeUnits: () => void;
    concordanceUnicodeFormat: () => void;
    pageLevelNumbering: () => void;
    pageSeperator: () => void;
    includeVolumeLabelInPath: () => void;
    emailOutputFormat: () => void;
    excludeEmailHeaders: () => void;
    includeOLEChildren: () => void;
    includeThreadAttach: () => void;
    startingDocumentId: () => void;
}

export interface ExportDisabled {
    nearDuplicatesProcessAttachments: () => boolean;
    includeThreadAttach: () => boolean;
    maxRecordsPerFile: () => boolean;
    includeFullText: () => boolean;
    exportToDb: () => boolean;
    noOfOutputFormats: () => number;
    pageLevelNumbering: () => boolean;
    tiffBW: () => boolean;
    includeStamp: () => boolean;
    includeTiffConversion: boolean;
    includePdfConversion: boolean;
    includeTextConversion: boolean;
    includeNativeFiles: boolean;
}

export interface ExportValidation {
    drObject: DrObject;
    exportStreamName: {
        check: (name: string) => void,
        checkCharacters: (name: string, exists?: boolean, empty?: boolean) => void,
        hasError: boolean,
        invalid: boolean,
        checkForErrors: () => void,
        errorMessage: string,
        errorType: string,
        hasValidCharacters: boolean,
        validateName: (name: string, isItemClick: boolean) => void,
        isNameValidated: boolean,
        lengthValidation: () => void
    };
    dataAreaHandle: {
        check: () => void,
        invalid: boolean,
        hasError: boolean,
        errorMessage: string
    };
    documentsToExport: {
        check: () => void,
        checkForErrors: () => void,
        invalid: boolean,
        hasError: boolean,
        errorMessage: string
    };
    nearDuplicatesThreshold: {
        check: () => void,
        invalid: boolean,
        hasError: boolean,
        errorMessage: string
    };
    nearDuplicatesMinTermCount: {
        check: () => void,
        invalid: boolean,
        hasError: boolean,
        errorMessage: string
    };
    dateFormat: {
        check: () => void,
        checkForErrors: () => void,
        invalid: boolean,
        errorMessage: string,
        hasError: boolean,
    };
    dateTimeDelimiter: {
        check: () => void,
        checkForErrors: () => void,
        invalid: boolean,
        errorMessage: string,
        hasError: boolean,
    };
    timeFormat: {
        check: () => void,
        checkForErrors: () => void,
        invalid: boolean,
        errorMessage: string,
        hasError: boolean,
    };
    tableName: {
        validateName: () => void,
        checkForErrors: () => void,
        isDisabled: () => boolean,
        isCheckboxDisabled: () => boolean,
        invalid: boolean,
        hasError: boolean,
        errorType: string,
        errorMessage: string,
        hasValidCharacters: boolean,
        lengthValidation: () => void
    };
    maxManifestRecords: {
        check: () => void,
        checkForErrors?: () => void,
        invalid: boolean,
        hasError: boolean,
        errorMessage: string
    };
    postProcessDataAreaHandle: {
        check: () => void,
        invalid: boolean,
        hasError: boolean,
        errorMessage: string
    };
    volumeLabel: {
        check: () => void,
        lengthValidation: () => void,
        invalid: boolean,
        errorMessage: string,
        hasError: boolean,
        errorType: string
    };
    documentIdPrefix: {
        check: (id?: string) => void,
        lengthValidation: () => void,
        invalid: boolean,
        errorMessage: string,
        hasError: boolean,
        errorType: string
    };
    basePath: {
        check: () => void,
        lengthValidation: () => void,
        invalid: boolean,
        errorMessage: string,
        errorType: string,
        hasError: boolean
    };
    startingDocumentId: {
        defaultStartingDocumentId: number,
        check: (event?: any) => void,
        parse: () => void,
        checkForErrors?: () => void,
        invalid: boolean,
        errorMessage: string,
        hasError: boolean,
        isFieldEmptyOnBlur?: boolean
    };
    directory: {
        nativeSubdirectoryInput: ExportInputName,
        textSubdirectoryInput: ExportInputName,
        pdfSubdirectoryInput: ExportInputName,
        tiffSubdirectoryInput: ExportInputName,
    };
    documentFolder: {
        check: () => void,
        invalid: boolean,
        errorMessage: string,
        hasError: boolean,
        errorType: string,
    };
    reviewProjectId: {
        check: (event?: any) => void,
        invalid: boolean,
        hasError: boolean,
        errorMessage: string
    };
    valid: () => boolean;
}

export class ExportInputName {
    invalid: boolean;
    errorMessage: string;
    maxChar = 50;
    errorType = 'error';
    hasError: boolean;
    name = '';
    private onChangeCallback?: () => void;
    constructor(private changeDetector: ChangeDetectorRef, value?: Partial<ExportInputName>, onChangeCallback?: () => void) {
        if (value) {
            Object.assign(this, value);
        }
        this.onChangeCallback = onChangeCallback;
    }

    check(): void {
        if (this.name) {
            const errorMessage = new DrObject().uiValidateName(this.name);
            if (errorMessage) {
                this.errorMessage = errorMessage;
                this.invalid = true;
                this.hasError = true;
            } else if (!this.errorMessage?.includes('Up to')) {
                this.invalid = false;
                this.hasError = false;
            }
        } else {
            this.invalid = false;
            this.hasError = false;
        }
        if (!this.errorMessage?.includes('Up to')) {
            this.errorType = 'error';
        }

        this.onChangeCallback?.();
    }

    lengthValidation(): void {
        this.changeDetector.detectChanges();
        if (this.name.length > this.maxChar) {
            // Reset invalid to force false→true transition so the popover
            // re-shows even after X dismiss or when invalid was already true
            this.invalid = false;
            this.changeDetector.detectChanges();
            this.name = this.name.substring(0, this.maxChar);
            this.check();
            this.errorMessage = `Up to ${this.maxChar} characters are allowed. \nOne or more characters were trimmed at the end.`;
            this.invalid = true;
            this.errorType = 'info';
        } else if (this.errorMessage?.includes('Up to')) {
            this.errorMessage = null;
            this.invalid = false;
            this.hasError = false;
        }

        this.onChangeCallback?.();
    }
    trimLeadingSpaces(): void {
        if (this.name) {
            this.name = this.name.trim();
        }

        this.onChangeCallback?.();
    }
}
export interface ExportDocumentsToExport {
    searchWrapper: SearchWrapper;
    allDocs: boolean;
    allDocsDisabled: boolean;
    allDocsChanged: () => void;
    selectTags: () => void;
    selectedTags: Array<Tag>;
    selectTagsDisabled: boolean;
    query: string;
    queryChanged: () => void;
    clearQuery: () => void;
}

export interface ExportStamping {
    init: () => void;
    TopLeft?: ProductionStamp;
    TopCenter?: ProductionStamp;
    TopRight?: ProductionStamp;
    BottomLeft?: ProductionStamp;
    BottomCenter?: ProductionStamp;
    BottomRight?: ProductionStamp;
    selectedStamp?: ProductionStamp;
    getStampClasses: (selectedStamp: ProductionStamp, selectedLine: number) => void;
    clear: (selectedStamp: ProductionStamp, event: MouseEvent) => void;
    edit: (selectedStamp: ProductionStamp, selectedLine: number) => void;
    save: () => void;
    cancel: () => void;
}

export class ExportStreamOptionsViewModel extends DrObject {

    static EXPORT_OPTION_TABLE_NAME_MAX_LENGTH = DrObject.DefaultMaximumNameCharacters;
    static DEFAULT_STARTING_ID = '0000000001';

    constructor(value?: Partial<ExportStreamOptions>) {
        super(DrObjectType.EXPORT_DATABASE_TABLE, SupportedRegExType.EXPORT_DATABASE_TABLE);
    }
}
